/*
 * Copyright (c) 2022-2024. Haodong JU
 */

import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'
import hillo from 'hillo'
import GlobalSettings, { getRootUrl } from '@/model/global/GlobalSettings'
import Category, { colorList } from './Category'
import Allergen from './Allergen'
import StringEntity from '@/model/dataLayer/api/common/StringEntity'
import AttributeGroup from './AttributeGroup'
import dayjs from 'dayjs'
import IKUtils from 'innerken-js-utils'
import i18n from '@/i18n'
import { keyBy } from 'lodash-es'
import PrinterGroup from '@/model/dataLayer/api/print/PrinterGroup'
import { defaultLanguage } from '@/model/global/DefaultLanguage'
import store from '@/model/global/store'
import NewPfandDish from '@/model/dataLayer/api/dish/NewPfandDish'

const entity = {
  id: {
    type: IKDataEntity.Types.Integer,
    displayName: 'id',
    form: false,
    requiredNew: false,
    header: false
  },
  image: {
    type: IKDataEntity.Types.Image,
    formConfig: {
      type: {
        root: () => getRootUrl() + GlobalSettings.dishImageRoot
      },
      required: false,
      hint: 'recommended_picture_size_512x512' // 建议图片尺寸为512*512
    },
    tableConfig: {
      filterable: false,
      sortable: false
    },
    displayName: 'Picture',
    header: true
  },
  color: {
    type: IKDataEntity.Types.Color,
    formConfig: {
      default: colorList[Math.round(Math.random() * 18)],
      required: false,
      hint: 'selected_color_will_be_used_in_orderDevice' // 此处选择的颜色将会显示在员工使用的点餐设备上
    },
    header: false,
    tableConfig: { filterable: false }
  },
  code: {
    displayName: 'ProductNr',
    tableConfig: {
      width: '160'
    },
    formConfig: {
      required: true,
      unique: true,
      hint: 'products_will_be_sorted_by_number_eg_1_1a_2'
    }
  },
  price: {
    type: IKDataEntity.Types.Float,
    formConfig: {
      default: '',
      overwriteRule: true,
      rule: [v => /^[0-9]+\.{0,1}[0-9]{0,2}$/.test(v) || i18n.t('pls_enter_the_correct_price')],
      hint: 'use_dot_as_the_decimal_point_Negative_prices_are_not_allowed'
    },

    header: false
  },
  dishName: {
    displayName: 'name',
    form: false,
    tableConfig: { overwrite: true }

  },

  langs: {
    displayName: 'name',
    type: IKDataEntity.Types.Group,
    formConfig: {
      groupName: 'product_info', // 产品信息
      default: defaultLanguage(),
      childLabelKey: 'lang',
      type: {}
    },
    tableConfig: {
      displayCondition: (item) => {
        return item.lang.toLowerCase() === GlobalSettings.getLang().toLowerCase()
      },
      displayChild: (key) => {
        return key === 'name'
      }
    },
    childKey: ['name', 'desc'],
    labelKey: 'lang',
    children: [StringEntity, StringEntity, StringEntity],
    header: false
  },

  categoryId: {
    displayName: 'dishesCategoryId', // prokuct_series
    tableConfig: { filterable: false },
    type: IKDataEntity.Types.Option,
    formConfig: {
      hint: 'dishBelongClassify', // 产品所属的分类
      type: {
        showButton: true,

        selectItems: async () => {
          return await Category.getList(true)
        },
        itemText: '_langsname'
      }
    }
  },
  printGroupId: {
    displayName: 'printCatName',
    type: IKDataEntity.Types.Option,
    formConfig: {
      hint: 'SelectPrinterHint',
      default: 1,
      type: {
        selectItems: async () => {
          return await PrinterGroup.getList(true)
        }
      }
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false
  },
  cookingTime: {
    displayName: 'PreparationTime',
    formConfig: {
      default: 0,
      overwriteRule: true,
      rule: [v => /^\d+$/.test(v) || i18n.t('EnterRightNumber')],
      hint: 'PreparationTimeRuleHint',
      required: false
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false
  },
  useTeaMaker: {
    displayName: 'MilkTeaMachine',
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: async () => {
          return booleanOptions
        },
        itemText: 'label',
        itemValue: 'value'
      },
      required: false,
      default: 0
    },
    header: false
  },
  mustShowCheckout: {
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: async () => {
          return booleanOptions
        },
        itemText: 'label',
        itemValue: 'value'
      },
      required: false,
      default: 0
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false
  },
  priceDisplay: {
    displayName: 'price',
    type: IKDataEntity.Types.Float,
    form: false // header: false,
  },
  AllergenId: {
    displayName: 'allergName', // 过敏药
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        showButton: true,
        selectItems: Allergen.getList,
        multiple: true
      },
      required: false
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false
  },
  attributeGroupId: {
    displayName: 'attributeGroupNames',
    type: IKDataEntity.Types.Option,
    formConfig: {
      required: false,
      onlyAction: true,
      type: {
        selectItems: AttributeGroup.getList,
        multiple: true,
        disabledItem: (item, editedItem) => {
          if (editedItem.inheritAttributeGroupId) {
            return ({
              ...item,
              disabled: editedItem.inheritAttributeGroupId.includes(item.id)
            })
          } else {
            return item
          }
        }
      }
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false,
    onlyAction: true
  },
  localAttributeGroupId: {
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: AttributeGroup.getList,
        multiple: true
      }
    },
    form: false,
    header: false
  },
  inheritAttributeGroupId: {
    displayName: 'AttributeGroupName',
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: AttributeGroup.getList,
        multiple: true
      }
    },
    form: false,
    header: false
  },
  dishWeight: {
    displayName: 'DishWeight',
    type: IKDataEntity.Types.Integer,
    formConfig: {
      default: 1,
      required: false,
      rule: [v => /^[0-9]\d*$/.test(v) || this.$t('EnterRightNumber')],
      hint: 'this_setting_will_change_the_click_times_on_the_product' // 此处的设置将会影响产品在包餐中所使用的点数。例如，将此处的点数设置为2，意味着每点一个本产品等于点了两个其他产品。
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false
  },
  isFavorite: {
    displayName: 'isFavorite', // 常用
    type: IKDataEntity.Types.Boolean,
    formConfig: {
      default: true,
      required: false
    },
    tableConfig: {
      width: '100px'
    }
  },
  isActive: {
    displayName: 'isActive', // 上架
    type: IKDataEntity.Types.Boolean,
    formConfig: {
      default: true,
      required: false
    },
    tableConfig: {
      width: '100px'
    }
  },
  extraPrintGroupIds: {
    displayName: 'extraPrintGroup',
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        multiple: true,
        selectItems: PrinterGroup.getList
      },
      hint: 'extrenal_printer_print_same_bill', // 如果在此设置了额外打印机，那么产品除了正常的出单外，还会额外在此处设置的打印机出一份相同的厨房单
      required: false
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false
  },
  productSource: {
    form: async () => {
      return store.state.adminMode === 'supermarket'
    },
    formConfig: {
      hint: '产品原产地',
      required: false
    },
    header: false
  },
  productClass: {
    form: async () => {
      return store.state.adminMode === 'supermarket'
    },
    formConfig: {
      hint: '产品等级',
      required: false
    },
    header: false
  },
  attrLimit: {
    type: IKDataEntity.Types.Integer,
    formConfig: {
      required: false,
      default: -1,
      hint: 'AdditivesHint'
    },
    form: async () => {
      return store.state.adminMode !== 'supermarket'
    },
    header: false
  },
  isPriceByKg: {
    type: IKDataEntity.Types.Boolean,
    formConfig: {
      default: true,
      required: false,
      hint: 'ScaleHintEAProducts'
    },
    form: async () => {
      return store.state.adminMode === 'supermarket'
    },
    header: async () => {
      return store.state.adminMode === 'supermarket'
    }
  },
  isPfandDish: {
    type: IKDataEntity.Types.Boolean,
    formConfig: {
      default: false,
      required: false,
      hint: 'ProductIncludesPfand'
    },
    // form: async () => {
    //   return store.state.adminMode === 'supermarket'
    // },
    header: false
  },
  usingPfandDishIds: {
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        multiple: true,
        selectItems: async () => {
          return NewPfandDish.getList(true)
        },
        itemText: 'dishName',
        itemValue: 'id'
      },
      required: false
    },
    // form: async () => {
    //   return store.state.adminMode === 'supermarket'
    // },
    header: false
  }

}

const config = {

  load: async function (filter) {
    const notTouchableDish = keyBy((await hillo.get('Dishes.php?op=cantChangeDish')).content, 'code')
    const systemDish = ['cashWithdraw', 'cashDeposit', 'gel', 'gk', 'lk']
    return (await hillo.get('Dishes.php', {
      lang: GlobalSettings.getLang(),
      onlyActive: 0,
      ...filter
    }))
      .content.filter(it => !systemDish.includes(it.code)).map(function (item) {
        item.displayCode = item.code.length > 5 ? item.code.slice(0, 5) + '...' : item.code
        item.id = item.dishId
        item.attributeGroupId = [
          item.inheritAttributeGroupId ? item.inheritAttributeGroupId.split(',') : [],
          item.localAttributeGroupId ? item.localAttributeGroupId.split(',') : []
        ].flat().join(',')
        item.usingPfandDishIds = item?.usingPfandDishIds ? item?.usingPfandDishIds.split(',') : []
        item.langs = item.langs.map(i => {
          return {
            ...i,
            desc: i.description
          }
        })
        item.editable = !notTouchableDish[item.code]
        item.priceDisplay = item.price
        return item
      })
  },
  add: async function (item) {
    if (store.state.adminMode !== 'supermarket') {
      item.attributeGroup = ''
      item.allergenGroup = [item?.AllergenId].flat().join(',') ?? ''
      item.extraPrintGroupIds = [item?.extraPrintGroupIds].flat().join(',') ?? ''
    } else {
      item.printGroupId = '10'
      item.extraPrintGroupIds = ''
    }
    item.productClass = ''
    item.productSource = ''
    // 和 edit 一样转成 PHP 能直接写库的值；原样发布尔值和数组时，数组会被写成字符串 "Array"
    item.isPfandDish = item.isPfandDish ? 1 : 0
    item.usingPfandDishIds = [item.usingPfandDishIds ?? []].flat()
      .filter(id => id != null && id !== '')
      .join(',')
    if (item.code === '自动生成') {
      const codes = await loadAllCode()
      for (let i = 0; i < 10000; i++) {
        if (!codes.includes(i.toString())) {
          item.code = i
        }
      }
      if (item.code === '自动生成') {
        item.code = dayjs().utcOffset()
      }
    }
    return (await hillo.postWithUploadFile('Dishes.php?op=add', {
      ...item,
      params: JSON.stringify(item)
    }, { showLoading: false })).content
  },

  remove: async function (id) {
    IKUtils.showLoading()
    try {
      const res = (await hillo.post('Dishes.php?op=delete', { id }))
      if (res.status === 'bad') {
        await hillo.post('Dishes.php?op=softDelete', { id })
        await hillo.post('ConsumeType.php?op=deleteBuffetPriceDishToConsumeType', { dishId: id }, { showLoading: false })
      }
    } catch (e) {
      await hillo.post('Dishes.php?op=softDelete', { id })
      await hillo.post('ConsumeType.php?op=deleteBuffetPriceDishToConsumeType', { dishId: id }, { showLoading: false })
    }
    return true
  },
  edit: async function (item) {
    const updateItem = Object.assign({}, item)
    const oldEx = updateItem.extraPrintGroupIds
    if (store.state.adminMode !== 'supermarket') {
      updateItem.productClass = ''
      updateItem.productSource = ''
      updateItem.attributeGroup = updateItem
        ?.attributeGroupId
        ?.filter(i => !updateItem.inheritAttributeGroupId.includes(i)).join(',') ?? ''
      updateItem.allergenGroup = updateItem?.AllergenId?.join(',') ?? ''
      updateItem.extraPrintGroupIds = updateItem?.extraPrintGroupIds?.join ? updateItem.extraPrintGroupIds.join(',') : ''
    } else {
      updateItem.printGroupId = '10'
      updateItem.extraPrintGroupIds = ''
    }
    if (updateItem.file) {
      updateItem.imageExt = updateItem.file.name.split('.')[1]
    }
    updateItem.isActive = updateItem.isActive ? 1 : 0
    updateItem.isPriceByKg = updateItem.isPriceByKg ? 1 : 0
    updateItem.isPfandDish = updateItem.isPfandDish ? 1 : 0
    updateItem.isFavorite = updateItem.isFavorite ? 1 : 0
    // 字段不存在时（如 dishSync 传入的对象）保持 undefined，PHP 收到 null 就不会改这一列
    if (updateItem.usingPfandDishIds !== undefined) {
      // load 会把库里的脏值 "Array" 拆成 ['Array']，这里一并去掉
      updateItem.usingPfandDishIds = [updateItem.usingPfandDishIds].flat()
        .filter(id => id != null && id !== '' && id !== 'Array')
        .join(',')
    }
    const res = await hillo.postWithUploadFile('Dishes.php?op=update', {
      ...updateItem,
      params: JSON.stringify(updateItem)
    },
    { showLoading: false })
    updateItem.extraPrintGroupIds = oldEx
    return res
  },
  nameBuilder: function (item) {
    return item.code + '.' + item.dishName
  },
  rowDisableFunction (item) {
    return !item.editable
  }

}

export async function loadAllCode () {
  return (await hillo.get('Dishes.php', {
    onlyActive: 0
  })).content.map(it => it.code)
}

const DishModel = IKDataEntity.ModelFactory(entity, config)
DishModel.getBuffet = async function () {
  return (await DishModel.getList()).filter(i => i.dishesCategoryTypeId === '8')
}
export default DishModel

export async function checkDishDetail (id) {
  const res = (await hillo.get('Dishes.php?op=withMod', { id })).content
  res.name = res.langs.find(it => it.lang.toLowerCase() === GlobalSettings.getLang().toLowerCase()) ?? ''
  return res
}

export async function printDishesTag (ids) {
  return (await hillo.post('Dishes.php?op=printDishesSticker', {
    codes: ids
  }))
}

export async function removeDishImage (id) {
  return (await hillo.post('Dishes.php?op=removeDishImage', {
    id
  }))
}
export const booleanOptions = [
  {
    label: i18n.t('Yes'),
    value: 1
  },
  {
    label: i18n.t('no'),
    value: 0
  }
]
